/* eslint-disable no-unused-vars */
import { Editor, mergeAttributes } from "@tiptap/core";
import { Image } from "@tiptap/extension-image";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { v4 as uuidv4 } from "uuid";
import { type KeyboardShortcutCommand } from "@tiptap/core";

import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { TrackImageDeletionPlugin } from "@/components/tiptap/plugins/delete-image";
import { isFileValid } from "@/components/tiptap/extensions/utils";
import { CustomImageNode } from "@/components/tiptap/extensions/custom-image/image-node";
import { type UploadImageExtensionStorage, type TFileHandler } from "@/types/tiptap";

export type InsertImageComponentProps = {
  file?: File;
  pos?: number;
  event: "insert";
};

export const getImageComponentImageFileMap = (editor: Editor) =>
  (editor.storage.imageComponent as UploadImageExtensionStorage | undefined)?.fileMap;

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    imageComponent: {
      insertImageComponent: ({ file, pos, event }: InsertImageComponentProps) => ReturnType;
      uploadImage: (file: File) => () => Promise<string> | undefined;
    };
  }
}

export const CustomImageExtension = (props: TFileHandler) => {
  const { upload, delete: deleteImage } = props;

  return Image.extend<Record<string, unknown>, UploadImageExtensionStorage>({
    name: "imageComponent",
    selectable: true,
    group: "block",
    atom: true,
    draggable: true,

    addAttributes() {
      return {
        ...this.parent?.(),
        width: {
          default: null,
        },
        src: {
          default: null,
        },
        height: {
          default: null,
        },
        ["id"]: {
          default: null,
        },
        aspectRatio: {
          default: null,
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: "img",
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      return ["img", mergeAttributes(HTMLAttributes)];
    },

    onCreate() {
      const imageSources = new Set<string>();
      this.editor.state.doc.descendants((node) => {
        if (node.type.name === this.name) {
          imageSources.add(node.attrs.src);
        }
      });
    },

    addKeyboardShortcuts() {
      return {
        ArrowDown: insertEmptyParagraphAtNodeBoundaries("down", this.name),
        ArrowUp: insertEmptyParagraphAtNodeBoundaries("up", this.name),
      };
    },

    addProseMirrorPlugins() {
      return [TrackImageDeletionPlugin(this.editor, deleteImage, this.name)];
    },

    addStorage() {
      return {
        fileMap: new Map(),
        deletedImageSet: new Map<string, boolean>(),
        uploadInProgress: false,
      };
    },

    addCommands() {
      return {
        insertImageComponent:
          (props: { file?: File; pos?: number; event: "insert" }) =>
          ({ commands }) => {
            // Early return if there's an invalid file being dropped
            if (props?.file && !isFileValid(props.file)) {
              return false;
            }

            // generate a unique id for the image to keep track of dropped
            // files' file data
            const fileId = uuidv4();

            const imageComponentImageFileMap = getImageComponentImageFileMap(this.editor);

            if (imageComponentImageFileMap) {
              if (props.event === "insert") {
                imageComponentImageFileMap.set(fileId, {
                  event: props.event,
                  hasOpenedFileInputOnce: false,
                });
              }
            }

            const attributes = {
              id: fileId,
            };

            if (props.pos) {
              return commands.insertContentAt(props.pos, {
                type: this.name,
                attrs: attributes,
              });
            }
            return commands.insertContent({
              type: this.name,
              attrs: attributes,
            });
          },
        uploadImage: (file: File) => async () => {
          const fileUrl = await upload(file);
          return fileUrl;
        },
      };
    },

    addNodeView() {
      return ReactNodeViewRenderer(CustomImageNode);
    },
  });
};

type Direction = "up" | "down";

function insertEmptyParagraphAtNodeBoundaries(direction: Direction, nodeType: string): KeyboardShortcutCommand {
  return ({ editor }) => {
    try {
      const { selection, doc } = editor.state;
      const { $from, $to } = selection;

      let targetNode: ProseMirrorNode | null = null;
      let targetNodePos: number | null = null;

      // check if the selection itself is the target node
      doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
        if (node.type.name === nodeType) {
          targetNode = node;
          targetNodePos = pos;
          return false; // stop iterating once the target node is found
        }
        return true;
      });

      if (targetNode === null || targetNodePos === null) return false;

      const docSize = doc.content.size; // get the size of the document

      switch (direction) {
        case "up": {
          const insertPosUp = targetNodePos;

          // ensure the insert position is within the document boundaries
          if (insertPosUp < 0 || insertPosUp > docSize) return false;

          if (insertPosUp === 0) {
            // if at the very start of the document, insert a new paragraph at the start
            editor.chain().insertContentAt(insertPosUp, { type: "paragraph" }).run();
            editor.chain().setTextSelection(insertPosUp).run(); // set the cursor to the new paragraph
          } else {
            // otherwise, check the node immediately before the target node
            const prevNode = doc.nodeAt(insertPosUp - 1);

            if (prevNode && prevNode.type.name === "paragraph") {
              // if the previous node is a paragraph, move the cursor there
              editor
                .chain()
                .setTextSelection(insertPosUp - 1)
                .run();
            } else {
              return false; // if the previous node is not a paragraph, do not proceed
            }
          }
          break;
        }

        case "down": {
          const insertPosDown = targetNodePos + (targetNode as ProseMirrorNode).nodeSize;

          // ensure the insert position is within the document boundaries
          if (insertPosDown < 0 || insertPosDown > docSize) return false;

          // check the node immediately after the target node
          const nextNode = doc.nodeAt(insertPosDown);

          if (nextNode && nextNode.type.name === "paragraph") {
            // if the next node is a paragraph, move the cursor to the end of it
            const endOfParagraphPos = insertPosDown + nextNode.nodeSize - 1;
            editor.chain().setTextSelection(endOfParagraphPos).run();
          } else if (!nextNode) {
            // if there is no next node (end of document), insert a new paragraph
            editor.chain().insertContentAt(insertPosDown, { type: "paragraph" }).run();
            editor
              .chain()
              .setTextSelection(insertPosDown + 1)
              .run(); // set the cursor to the new paragraph
          } else {
            return false; // if the next node is not a paragraph, do not proceed
          }
          break;
        }

        default:
          return false; // if the direction is not recognized, do not proceed
      }

      return true; // return true if the operation was successful
    } catch (error) {
      console.error(`An error occurred while inserting a line ${direction} the ${nodeType}:`, error);
      return false; // return false if an error occurred
    }
  };
}
